import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle, Upload, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

const readSource = () => {
  const params = new URLSearchParams(window.location.search);
  const source = {};
  ["source", "medium", "campaign", "ref"].forEach((k) => {
    const v = params.get(k);
    if (v) source[k] = v;
  });
  return source;
};

const FieldRenderer = ({ field, value, onChange, disabled, onUpload }) => {
  const common = { id: `f-${field.key}`, "data-testid": `field-${field.key}`, disabled };
  switch (field.type) {
    case "short_text":
    case "email":
    case "phone":
    case "url":
      return (
        <input
          {...common}
          type={field.type === "email" ? "email" : field.type === "url" ? "url" : "text"}
          className="apply-input"
          placeholder={field.placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "number":
      return (
        <input
          {...common}
          type="number"
          className="apply-input"
          placeholder={field.placeholder || ""}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        />
      );
    case "long_text":
      return (
        <textarea
          {...common}
          className="apply-input min-h-[110px]"
          placeholder={field.placeholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "date":
      return (
        <input
          {...common}
          type="date"
          className="apply-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "dropdown":
      return (
        <select {...common} className="apply-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "radio":
      return (
        <div className="flex flex-col gap-2">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={field.key}
                data-testid={`field-${field.key}-${o}`}
                checked={value === o}
                onChange={() => onChange(o)}
                disabled={disabled}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
        onChange(next);
      };
      return (
        <div className="flex flex-col gap-2">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid={`field-${field.key}-${o}`}
                checked={arr.includes(o)}
                onChange={() => toggle(o)}
                disabled={disabled}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    }
    case "yes_no":
      return (
        <div className="flex gap-2">
          {[
            { l: "Yes", v: true },
            { l: "No", v: false },
          ].map((opt) => (
            <button
              type="button"
              key={opt.l}
              data-testid={`field-${field.key}-${opt.l.toLowerCase()}`}
              disabled={disabled}
              onClick={() => onChange(opt.v)}
              className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded border transition-colors ${
                value === opt.v
                  ? "bg-[#17B352] text-black border-[#17B352]"
                  : "border-[rgba(255,255,255,0.14)] text-[#A1A1AA] hover:border-[#17B352]/50"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      );
    case "consent":
      return (
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            data-testid={`field-${field.key}`}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="mt-1"
          />
          <span>{field.label}</span>
        </label>
      );
    case "file":
      return <FileFieldControl field={field} value={value} onChange={onChange} onUpload={onUpload} disabled={disabled} />;
    default:
      return null;
  }
};

const FileFieldControl = ({ field, value, onChange, onUpload, disabled }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const info = await onUpload(file);
      onChange(info);
    } catch (err) {
      setError(err.message || "Upload failed");
      toast.error(err.message || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className={`inline-flex items-center gap-2 cursor-pointer px-4 py-2 rounded border border-dashed transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed border-[#3F3F46]" : "border-[#17B352]/40 hover:bg-[#17B352]/5"
      }`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <span className="mono-font text-xs uppercase tracking-wider">
          {busy ? "Uploading…" : value?.filename ? "Replace file" : "Upload file"}
        </span>
        <input
          type="file"
          data-testid={`field-${field.key}-input`}
          className="hidden"
          onChange={pick}
          disabled={disabled || busy}
        />
      </label>
      {value?.filename && (
        <p className="mono-font text-[10px] text-[#32D74B] mt-2">Uploaded: {value.filename}</p>
      )}
      {error && <p className="mono-font text-[10px] text-[#FF375F] mt-2">{error}</p>}
    </div>
  );
};

const ApplyPage = () => {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: "", campaign: null });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/apply/${slug}`);
        setState({ loading: false, error: "", campaign: data });
      } catch (e) {
        const detail = e.response?.data?.detail || "Campaign not found or closed";
        setState({ loading: false, error: detail, campaign: null });
      }
    })();
  }, [slug]);

  const setAnswer = useCallback((key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
  }, []);

  const uploadFile = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/apply/${slug}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();

    // Client validation
    const missing = [];
    (state.campaign?.form?.sections || []).forEach((s) => {
      s.fields.forEach((f) => {
        if (f.state !== "required") return;
        const val = answers[f.key];
        if (f.type === "yes_no") {
          if (typeof val !== "boolean") missing.push(f);
        } else if (f.type === "consent") {
          if (val !== true) missing.push(f);
        } else if (f.type === "file") {
          if (!val?.storage_key) missing.push(f);
        } else if (isEmpty(val)) {
          missing.push(f);
        }
      });
    });
    if (missing.length) {
      setValidationErrors(missing.map((f) => f.label));
      toast.error(`Please complete: ${missing.slice(0, 3).map((f) => f.label).join(", ")}${missing.length > 3 ? "…" : ""}`);
      return;
    }
    setValidationErrors([]);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/apply/${slug}`, {
        answers,
        source: readSource(),
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.fields) {
        toast.error(`Missing: ${detail.fields.join(", ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#17B352]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5] flex items-center justify-center px-6">
        <div className="glass-card rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-[#FF375F] mx-auto mb-4" />
          <h1 className="text-2xl mb-2">Application unavailable</h1>
          <p className="mono-font text-xs text-[#52525B] mb-6">{state.error}</p>
          <Link to="/" className="glow-button inline-block text-sm py-2 px-5">Back to Home</Link>
        </div>
      </div>
    );
  }

  const c = state.campaign;

  if (result) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5] px-6 py-16">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-10 text-center">
            <CheckCircle className="w-14 h-14 text-[#32D74B] mx-auto mb-4" />
            <h1 className="text-3xl mb-3">{result.success?.title || "Application received"}</h1>
            <p className="text-[#A1A1AA] mb-4">{result.success?.body}</p>
            <p className="mono-font text-xs text-[#17B352] mb-8">Reference: {result.application_code}</p>
            <Link to="/" className="glow-button inline-block text-sm py-2 px-5">Return to Graphikpro</Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5] px-6 py-12">
      <style>{`
        .apply-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          padding: 0.6rem 0.8rem;
          color: #E5E5E5;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }
        .apply-input:focus { outline: none; border-color: #17B352; }
      `}</style>

      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] hover:text-white">
            ← Graphikpro
          </Link>
          <h1 className="text-3xl sm:text-4xl mt-3">{c.public_title}</h1>
          {c.role && <p className="mono-font text-xs uppercase tracking-wider text-[#17B352] mt-1">{c.role}</p>}
          {c.description && <p className="text-[#A1A1AA] mt-4">{c.description}</p>}
          {c.form?.welcome?.body && (
            <p className="text-[#A1A1AA] mt-4 border-l-2 border-[#17B352]/40 pl-4">
              {c.form.welcome.body}
            </p>
          )}
          {c.form?.welcome?.estimated_time_min && (
            <p className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] mt-2">
              Estimated time: {c.form.welcome.estimated_time_min} min
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-6">
          {(c.form?.sections || []).map((s) => (
            <div key={s.key} className="glass-card rounded-xl p-6 space-y-5" data-testid={`section-${s.key}`}>
              <div>
                <h2 className="text-xl">{s.title}</h2>
                {s.description && <p className="mono-font text-xs text-[#52525B] mt-1">{s.description}</p>}
              </div>
              {s.fields.map((f) => {
                if (f.type === "section_heading") {
                  return <h3 key={f.key} className="text-base border-b border-[rgba(255,255,255,0.06)] pb-2">{f.label}</h3>;
                }
                if (f.type === "info_text") {
                  return <p key={f.key} className="mono-font text-xs text-[#52525B]">{f.label}</p>;
                }
                return (
                  <div key={f.key} className="space-y-1.5">
                    {f.type !== "consent" && (
                      <label htmlFor={`f-${f.key}`} className="mono-font text-[10px] uppercase tracking-wider text-[#A1A1AA] block">
                        {f.label}{f.state === "required" && <span className="text-[#FF375F] ml-1">*</span>}
                      </label>
                    )}
                    <FieldRenderer
                      field={f}
                      value={answers[f.key]}
                      onChange={(v) => setAnswer(f.key, v)}
                      disabled={submitting}
                      onUpload={uploadFile}
                    />
                    {f.help && <p className="mono-font text-[10px] text-[#52525B]">{f.help}</p>}
                  </div>
                );
              })}
            </div>
          ))}

          {validationErrors.length > 0 && (
            <div className="glass-card rounded-xl p-4 border border-[#FF375F]/30">
              <p className="mono-font text-xs text-[#FF375F] mb-1">Please complete the following required fields:</p>
              <ul className="mono-font text-xs text-[#A1A1AA] list-disc list-inside">
                {validationErrors.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-2">
            <p className="mono-font text-[10px] uppercase tracking-wider text-[#52525B]">
              Your data is handled per Graphikpro&apos;s{" "}
              <Link to="/privacy" target="_blank" className="text-[#17B352] hover:underline">Privacy Policy</Link>.
            </p>
            <button
              type="submit"
              data-testid="apply-submit-btn"
              disabled={submitting}
              className="glow-button text-sm py-2 px-6 flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyPage;
